import { combineEpics, ofType } from 'redux-observable';
import { concat, from, of } from 'rxjs';
import { map as rxjsMap, switchMap } from 'rxjs/operators';
import { teladocApi, authToken } from '@td/api';
import { has, omit, flatten, values, pick, join, map, reduce } from 'lodash';

import { createRequestEpic } from '@td/utils';
import {
  providerSurgeIncentiveCreateForm,
  providerSurgeIncentiveSearchRequest,
  providerSurgeIncentiveSetIncentives,
  showFlashMessage,
  providerSurgeIncentiveEditForm
} from '../actions';
import {
  PROVIDER_SURGE_INCENTIVE_CREATE_REQUEST,
  PROVIDER_SURGE_INCENTIVE_SEARCH_REQUEST,
  PROVIDER_SURGE_INCENTIVE_UPDATE_SEARCH_FILTERS,
  PROVIDER_SURGE_INCENTIVE_DELETE_REQUEST,
  PROVIDER_SURGE_INCENTIVE_EDIT_REQUEST
} from '../action-types';

const SPECIFIC_ERROR_KEYS = ['date_range'];

const PROVIDER_INCENTIVE_COSTS_URL = '/v4/provider_incentive_costs';

const getCommonErrors = errorData => {
  const commonErrors = omit(errorData.errors, SPECIFIC_ERROR_KEYS);

  return flatten(values(commonErrors));
};

const getSpecificErrors = errorData => {
  const specificErrors = pick(errorData.errors, SPECIFIC_ERROR_KEYS);

  const result = [];

  if (has(specificErrors, 'date_range')) {
    result.push(
      `Date range for (${errorData.error_record.state_cd}): ${join(specificErrors.date_range)}`
    );
  }

  return result;
};

const normalizeSurgeIncentives = surgeIncentives => {
  const allIds = map(surgeIncentives, surgeIncentive => surgeIncentive.provider_incentive_cost_id);
  const byId = reduce(
    surgeIncentives,
    (result, surgeIncentive) => ({
      ...result,
      [surgeIncentive.provider_incentive_cost_id]: pick(surgeIncentive, [
        'provider_incentive_cost_id',
        'start_date',
        'end_date',
        'state_code',
        'amount'
      ])
    }),
    {}
  );

  return [allIds, byId];
};

export const createSurgeIncentivesEpic = createRequestEpic(
  PROVIDER_SURGE_INCENTIVE_CREATE_REQUEST,
  ({ action }) => {
    const { startDate, endDate, amount, states } = action.payload;

    const surgeIncentives = {
      provider_incentive_costs: states.map(stateCode => ({
        provider_incentive_cost: {
          state_code: stateCode,
          amount,
          start_date: startDate,
          end_date: endDate,
          context_cd: 'PROVIDERINCENTIVECONTEXT_OPEN'
        }
      }))
    };

    return from(
      teladocApi.post(PROVIDER_INCENTIVE_COSTS_URL, surgeIncentives, authToken.get())
    );
  },
  [
    error => {
      const errorData = error.response.data;

      const errors = [...getCommonErrors(errorData), ...getSpecificErrors(errorData)];

      return concat(
        of(
          providerSurgeIncentiveCreateForm.setFormErrors(errors.length ? errors : [error.message])
        ),
        of(showFlashMessage('Failed to create incentives, something went wrong.'))
      );
    }
  ]
);

export const createSurgeIncentivesSuccessEpic = action$ =>
  action$.pipe(
    ofType(PROVIDER_SURGE_INCENTIVE_CREATE_REQUEST.SUCCESS),
    switchMap(action => {
      const items = action.payload.provider_incentive_costs;

      const [allIds, byId] = normalizeSurgeIncentives(items);

      return concat(
        of(providerSurgeIncentiveSetIncentives(byId, allIds, items.length)),
        of(showFlashMessage('Created incentives.'))
      );
    }));

export const searchSurgeIncentivesStartEpic = action$ =>
  action$.pipe(
    ofType(
      PROVIDER_SURGE_INCENTIVE_UPDATE_SEARCH_FILTERS,
      PROVIDER_SURGE_INCENTIVE_DELETE_REQUEST.SUCCESS,
      PROVIDER_SURGE_INCENTIVE_EDIT_REQUEST.SUCCESS
    ),
    rxjsMap(() => providerSurgeIncentiveSearchRequest.start()));

export const searchSurgeIncentivesEpic = createRequestEpic(
  PROVIDER_SURGE_INCENTIVE_SEARCH_REQUEST,
  ({ store }) => {
    const state = store.value;

    const { searchFilters } = state.providerSurgeIncentive;

    return from(
      teladocApi.get(PROVIDER_INCENTIVE_COSTS_URL, searchFilters, authToken.get())
    );
  },
  [() => of(showFlashMessage('Failed to filter incentives, something went wrong.'))]
);

export const searchSurgeIncentivesSuccessEpic = action$ =>
  action$.pipe(
    ofType(PROVIDER_SURGE_INCENTIVE_SEARCH_REQUEST.SUCCESS),
    rxjsMap(action => {
      const { items, total_entries: totalEntries } = action.payload;

      const [allIds, byId] = normalizeSurgeIncentives(items);

      return providerSurgeIncentiveSetIncentives(byId, allIds, totalEntries);
    }));

export const deleteSurgeIncentiveEpic = createRequestEpic(
  PROVIDER_SURGE_INCENTIVE_DELETE_REQUEST,
  ({ action }) =>
    from(
      teladocApi.delete(`${PROVIDER_INCENTIVE_COSTS_URL}/${action.payload.id}`, authToken.get())
    ),
  [() => of(showFlashMessage('Failed to delete incentive, something went wrong.'))]
);

export const deleteSurgeIncentiveSuccessEpic = action$ =>
  action$.pipe(
    ofType(PROVIDER_SURGE_INCENTIVE_DELETE_REQUEST.SUCCESS),
    rxjsMap(() => showFlashMessage('Deleted incentive.')));

export const editSurgeIncentiveEpic = createRequestEpic(
  PROVIDER_SURGE_INCENTIVE_EDIT_REQUEST,
  ({ action }) => {
    const { id, amount, endDate, startDate } = action.payload;

    return from(
      teladocApi.put(
        `${PROVIDER_INCENTIVE_COSTS_URL}/${id}`,
        {
          provider_incentive_cost: {
            amount,
            end_date: endDate,
            start_date: startDate
          }
        },
        authToken.get()
      )
    );
  },
  [
    error => {
      const errorData = error.response.data;

      const errors = [...getCommonErrors(errorData), ...getSpecificErrors(errorData)];

      return concat(
        of(
          providerSurgeIncentiveEditForm.setFormErrors(errors.length ? errors : [error.message])
        ),
        of(showFlashMessage('Failed to edit incentive, something went wrong.'))
      );
    }
  ]
);

export const editSurgeIncentiveSuccessEpic = action$ =>
  action$.pipe(
    ofType(PROVIDER_SURGE_INCENTIVE_EDIT_REQUEST.SUCCESS),
    rxjsMap(() => showFlashMessage('Edited incentive.')));

export default combineEpics(
  createSurgeIncentivesEpic,
  createSurgeIncentivesSuccessEpic,
  searchSurgeIncentivesStartEpic,
  searchSurgeIncentivesEpic,
  searchSurgeIncentivesSuccessEpic,
  deleteSurgeIncentiveEpic,
  deleteSurgeIncentiveSuccessEpic,
  editSurgeIncentiveEpic,
  editSurgeIncentiveSuccessEpic
);
